import type { ColumnDef, AggregationFn, SortingState, ColumnPinningState, ExpandedState } from "@tanstack/react-table";
import type { TanTableRenderModel, TanTableVizSpecV1 } from "./tanTableSpec.js";
import type { TableColumnSpecV1, TableDataType } from "./tableSpec.js";
import { buildFormatter } from "./valueFormat.js";
import type { AtlasValueFormat } from "./valueFormat.js";

export type TanTableColumnMeta = {
  align?: "left" | "center" | "right";
  conditionalFormat?: TableColumnSpecV1["conditionalFormat"];
  suppressDuplicates?: boolean;
};

type RowArray = unknown[];

function tableDataTypeToAtlasStyle(dt: TableDataType | undefined): AtlasValueFormat["style"] | undefined {
  switch (dt) {
    case "int":
    case "decimal":
    case "currency":
    case "percent":
    case "date":
    case "datetime":
      return dt;
    default:
      return undefined;
  }
}

const AGG_FNS: Record<string, AggregationFn<RowArray>> = {
  sum: (_columnId, _leafRows, childRows) => {
    let total = 0;
    for (const row of childRows) {
      const v = row.getValue<unknown>(_columnId);
      if (typeof v === "number" && Number.isFinite(v)) total += v;
    }
    return total;
  },
  avg: (_columnId, _leafRows, childRows) => {
    let total = 0;
    let count = 0;
    for (const row of childRows) {
      const v = row.getValue<unknown>(_columnId);
      if (typeof v === "number" && Number.isFinite(v)) {
        total += v;
        count++;
      }
    }
    return count > 0 ? total / count : null;
  },
  min: (_columnId, _leafRows, childRows) => {
    let result: number | null = null;
    for (const row of childRows) {
      const v = row.getValue<unknown>(_columnId);
      if (typeof v === "number" && Number.isFinite(v)) {
        result = result === null ? v : Math.min(result, v);
      }
    }
    return result;
  },
  max: (_columnId, _leafRows, childRows) => {
    let result: number | null = null;
    for (const row of childRows) {
      const v = row.getValue<unknown>(_columnId);
      if (typeof v === "number" && Number.isFinite(v)) {
        result = result === null ? v : Math.max(result, v);
      }
    }
    return result;
  },
  count: (_columnId, _leafRows, childRows) => childRows.length,
};

const cellFormatterCache = new WeakMap<TanTableVizSpecV1["columns"][number], (v: unknown) => string>();

export function formatCellValue(value: unknown, col: TanTableVizSpecV1["columns"][number]): string {
  if (col.dataType === "string") {
    if (value == null) return col.format?.nullDisplay ?? "";
    return String(value);
  }
  let fn = cellFormatterCache.get(col);
  if (!fn) {
    const fmt = col.format;
    const spec: AtlasValueFormat = {
      style: tableDataTypeToAtlasStyle(col.dataType) ?? (fmt?.currency ? "currency" : undefined),
      currency: fmt?.currency,
      decimals: fmt?.decimals,
      percentScale: fmt?.percentScale,
      dateFormat: fmt?.dateFormat ?? fmt?.datetimeFormat,
      nullDisplay: fmt?.nullDisplay,
    };
    fn = buildFormatter(spec);
    cellFormatterCache.set(col, fn);
  }
  return fn(value);
}

export type ConditionalFormatResult = {
  bgColor?: string;
  textColor?: string;
  icon?: string;
};

const ICON_MAP: Record<string, string> = {
  up: "▲", down: "▼", dot: "●", warning: "⚠",
};

export function evaluateConditionalFormat(
  rules: NonNullable<TableColumnSpecV1["conditionalFormat"]>,
  rawValue: unknown,
): ConditionalFormatResult | null {
  const num = typeof rawValue === "number" ? rawValue : typeof rawValue === "string" ? Number(rawValue) : NaN;
  for (const rule of rules) {
    let match = false;
    const v = typeof rule.value === "number" ? rule.value : Number(rule.value);
    switch (rule.op) {
      case "gt": match = num > v; break;
      case "gte": match = num >= v; break;
      case "lt": match = num < v; break;
      case "lte": match = num <= v; break;
      case "eq": match = typeof rawValue === "string" ? rawValue === String(rule.value) : num === v; break;
      case "between": {
        const max = typeof rule.maxValue === "number" ? rule.maxValue : Number(rule.maxValue);
        match = num >= v && num <= max;
        break;
      }
    }
    if (!match) continue;
    const result: ConditionalFormatResult = {};
    if (rule.color) {
      if (rule.target === "cell") result.bgColor = rule.color;
      else if (rule.target === "text") result.textColor = rule.color;
      else if (rule.target === "icon" && rule.icon) result.icon = ICON_MAP[rule.icon] ?? "";
      if (rule.target === "icon" && rule.color) result.textColor = rule.color;
    }
    if (rule.target === "icon" && rule.icon && !result.icon) {
      result.icon = ICON_MAP[rule.icon] ?? "";
    }
    return result;
  }
  return null;
}

export function buildColumnDefs(model: TanTableRenderModel): ColumnDef<RowArray, unknown>[] {
  const spec = model.tableSpec;
  const colSpecById = new Map(spec.columns.map((c) => [c.id, c]));
  const colSpecByField = new Map(spec.columns.map((c) => [c.field, c]));
  const measureById = new Map((spec.measures ?? []).map((m) => [m.id, m]));
  const measureByField = new Map((spec.measures ?? []).map((m) => [m.field, m]));

  const pivotGroups = new Map<string, { label: string; children: ColumnDef<RowArray, unknown>[] }>();
  const topLevel: ColumnDef<RowArray, unknown>[] = [];

  const measureCount = (spec.measures ?? []).length;
  const singleMeasurePivot = measureCount <= 1;

  for (let ci = 0; ci < model.columns.length; ci++) {
    const col = model.columns[ci];
    let colSpec = colSpecById.get(col.key) ?? colSpecByField.get(col.key);
    let measure = measureById.get(col.key) ?? measureByField.get(col.key);

    if (!measure && col.pivotGroupLabel) {
      for (const m of spec.measures ?? []) {
        if (col.key.endsWith(`_${m.id}`) || col.key.endsWith(`_${m.field}`)) {
          measure = m;
          break;
        }
      }
    }

    if (!colSpec && measure) {
      colSpec = colSpecById.get(measure.id) ?? colSpecByField.get(measure.field);
    }

    const resolvedFormat = colSpec?.format ?? measure?.format;
    const formatProxy = resolvedFormat
      ? { ...colSpec ?? { id: col.key, field: col.key }, format: resolvedFormat } as TanTableVizSpecV1["columns"][number]
      : colSpec;

    const agg = measure?.agg ?? "sum";

    const displayLabel = singleMeasurePivot && col.pivotGroupLabel
      ? col.pivotGroupLabel
      : col.label;

    const rendererKind = colSpec?.renderer?.kind;
    const rendererParams = colSpec?.renderer?.params;

    const meta: TanTableColumnMeta = {};
    if (colSpec?.align) meta.align = colSpec.align;
    const conditionalFormat = measure?.conditionalFormat?.length
      ? measure.conditionalFormat
      : colSpec?.conditionalFormat?.length
        ? colSpec.conditionalFormat
        : undefined;
    if (conditionalFormat) meta.conditionalFormat = conditionalFormat;
    if (colSpec?.suppressDuplicates) meta.suppressDuplicates = true;

    const def: ColumnDef<RowArray, unknown> = {
      id: col.key,
      accessorFn: (row) => row[ci],
      header: displayLabel,
      cell: (info) => {
        const raw = info.getValue();
        if (info.row.getIsGrouped() && info.column.getIsGrouped()) {
          return String(raw ?? "");
        }
        const formatted = formatProxy
          ? formatCellValue(raw, formatProxy)
          : typeof raw === "number"
            ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(raw)
            : String(raw ?? "");
        if (rendererKind === "link") {
          let href = rendererParams?.href != null ? String(rendererParams.href) : "#";
          href = href.replace(/\{(\w+)\}/g, (_match, colId) => {
            const idx = model.columns.findIndex((c) => c.key === colId);
            if (idx >= 0) {
              const rowData = info.row.original as unknown[];
              return encodeURIComponent(String(rowData[idx] ?? ""));
            }
            return "";
          });
          return { __tanRender: "link" as const, text: formatted, href };
        }
        if (rendererKind === "badge") {
          const color = rendererParams?.color != null ? String(rendererParams.color) : undefined;
          return { __tanRender: "badge" as const, text: formatted, color };
        }
        return formatted;
      },
      size: colSpec?.width ?? 150,
      minSize: colSpec?.minWidth ?? 60,
      maxSize: colSpec?.maxWidth ?? 800,
      enableResizing: colSpec?.resizable !== false,
      enableSorting: colSpec?.sortable !== false,
      enableColumnFilter: true,
      enablePinning: colSpec?.pinned === "left",
      meta: Object.keys(meta).length > 0 ? meta : undefined,
    };

    if (measure || col.type === "number") {
      if (colSpec?.excludeFromTotals) {
        // Explicitly suppress aggregation so TanStack cannot compute or display a subtotal.
        def.aggregationFn = () => null;
        def.aggregatedCell = () => null;
      } else {
        def.aggregationFn = AGG_FNS[agg] ?? AGG_FNS.sum;
        def.aggregatedCell = (info) => {
          const raw = info.getValue();
          if (formatProxy) return formatCellValue(raw, formatProxy);
          if (typeof raw === "number") {
            return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(raw);
          }
          return String(raw ?? "");
        };
      }
    }

    if (col.pivotGroupLabel && !singleMeasurePivot) {
      let group = pivotGroups.get(col.pivotGroupLabel);
      if (!group) {
        group = { label: col.pivotGroupLabel, children: [] };
        pivotGroups.set(col.pivotGroupLabel, group);
      }
      group.children.push(def);
    } else {
      topLevel.push(def);
    }
  }

  for (const [label, group] of pivotGroups) {
    topLevel.push({
      id: `pivot_group_${label}`,
      header: label,
      columns: group.children,
    });
  }

  const columnGroupDefs = spec.columnGroups;
  if (columnGroupDefs && columnGroupDefs.length > 0) {
    const specIdToDefId = new Map<string, string>();
    for (const sc of spec.columns) {
      const renderCol = model.columns.find((rc) => rc.key === sc.field);
      if (renderCol) specIdToDefId.set(sc.id, renderCol.key);
    }

    const defIdToGroup = new Map<string, typeof columnGroupDefs[number]>();
    for (const cg of columnGroupDefs) {
      for (const cid of cg.columnIds) {
        const defId = specIdToDefId.get(cid);
        if (defId) defIdToGroup.set(defId, cg);
      }
    }
    const defById = new Map(topLevel.map((d) => [d.id, d]));
    const emittedGroups = new Set<string>();
    const result: ColumnDef<RowArray, unknown>[] = [];

    for (const def of topLevel) {
      const cg = defIdToGroup.get(def.id as string);
      if (cg) {
        if (!emittedGroups.has(cg.id)) {
          emittedGroups.add(cg.id);
          const children = cg.columnIds
            .map((cid) => {
              const defId = specIdToDefId.get(cid);
              return defId ? defById.get(defId) : undefined;
            })
            .filter(Boolean) as ColumnDef<RowArray, unknown>[];
          if (children.length > 0) {
            result.push({ id: `col_group_${cg.id}`, header: cg.label, columns: children });
          }
        }
      } else {
        result.push(def);
      }
    }

    return result;
  }

  return topLevel;
}

export function buildColumnFormatters(model: TanTableRenderModel): Array<(value: unknown) => string> {
  const spec = model.tableSpec;
  const colSpecById = new Map(spec.columns.map((c) => [c.id, c]));
  const colSpecByField = new Map(spec.columns.map((c) => [c.field, c]));
  const measureById = new Map((spec.measures ?? []).map((m) => [m.id, m]));
  const measureByField = new Map((spec.measures ?? []).map((m) => [m.field, m]));

  return model.columns.map((col) => {
    let colSpec = colSpecById.get(col.key) ?? colSpecByField.get(col.key);
    let measure = measureById.get(col.key) ?? measureByField.get(col.key);
    if (!measure && col.pivotGroupLabel) {
      for (const m of spec.measures ?? []) {
        if (col.key.endsWith(`_${m.id}`) || col.key.endsWith(`_${m.field}`)) { measure = m; break; }
      }
    }
    if (!colSpec && measure) {
      colSpec = colSpecById.get(measure.id) ?? colSpecByField.get(measure.field);
    }
    const resolvedFormat = colSpec?.format ?? measure?.format;
    const proxy = resolvedFormat
      ? { ...colSpec ?? { id: col.key, field: col.key }, format: resolvedFormat } as TanTableVizSpecV1["columns"][number]
      : colSpec;
    if (proxy) return (v: unknown) => formatCellValue(v, proxy);
    return (v: unknown) => {
      if (typeof v === "number") return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(v);
      return String(v ?? "");
    };
  });
}

export function getFoldControlFields(model: TanTableRenderModel): Array<{ field: string; label: string }> {
  const spec = model.tableSpec;
  const fc = spec.foldControl;
  if (!fc || !fc.fields?.length) return [];

  const colSpecByField = new Map(spec.columns.map((c) => [c.field, c]));
  const colSpecById = new Map(spec.columns.map((c) => [c.id, c]));
  const colKeySet = new Set(model.columns.map((c) => c.key));

  return fc.fields
    .filter((f) => colKeySet.has(f))
    .map((field) => {
      const colSpec = colSpecByField.get(field) ?? colSpecById.get(field);
      return { field, label: colSpec?.label ?? field };
    });
}

export function resolveGrouping(_model: TanTableRenderModel, activeFields: string[]): string[] {
  return activeFields;
}

export function buildInitialSorting(model: TanTableRenderModel): SortingState {
  const spec = model.tableSpec;
  if (!spec.sorting?.length) return [];
  return spec.sorting.map((s) => ({ id: s.columnId, desc: s.desc === true }));
}

export function buildColumnPinning(model: TanTableRenderModel): ColumnPinningState {
  const spec = model.tableSpec;
  const left: string[] = [];
  for (const col of spec.columns) {
    if (col.pinned === "left") left.push(col.id);
  }
  return { left, right: [] };
}

export function buildInitialExpanded(model: TanTableRenderModel): ExpandedState {
  const spec = model.tableSpec;
  if (spec.expandedGroupKeys?.length) {
    const expanded: Record<string, boolean> = {};
    for (const key of spec.expandedGroupKeys) expanded[key] = true;
    return expanded;
  }
  const depth = spec.totals?.defaultExpandDepth;
  if (typeof depth === "number" && depth >= 0) return true;
  return true;
}

function asFiniteNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

export function computeGrandTotal(
  model: TanTableRenderModel,
  rows: ReadonlyArray<RowArray> = model.rows,
): unknown[] | null {
  const spec = model.tableSpec;
  if (!spec.totals?.grandTotals) return null;
  if (rows.length === 0) return null;

  const measures = spec.measures ?? [];
  const measureById = new Map(measures.map((m) => [m.id, m]));
  const measureByField = new Map(measures.map((m) => [m.field, m]));
  const measureIds = new Set(measures.map((m) => m.id));
  const measureFields = new Set(measures.map((m) => m.field));
  const colSpecById = new Map(spec.columns.map((c) => [c.id, c]));
  const colSpecByField = new Map(spec.columns.map((c) => [c.field, c]));

  const result: unknown[] = new Array(model.columns.length).fill(null);

  for (let ci = 0; ci < model.columns.length; ci++) {
    const col = model.columns[ci];
    const isMeasure = measureIds.has(col.key) || measureFields.has(col.key) || col.type === "number";
    if (!isMeasure) continue;
    const colSpec = colSpecById.get(col.key) ?? colSpecByField.get(col.key);
    if (colSpec?.excludeFromTotals) continue;

    const measure = measureById.get(col.key) ?? measureByField.get(col.key);
    const agg = measure?.agg ?? "sum";

    let sum = 0;
    let count = 0;
    for (const row of rows) {
      const v = asFiniteNumber(row[ci]);
      if (v !== null) {
        sum += v;
        count++;
      }
    }
    if (count === 0) { result[ci] = null; continue; }

    if (agg === "avg") result[ci] = sum / count;
    else if (agg === "min") {
      let min = Infinity;
      for (const row of rows) { const v = asFiniteNumber(row[ci]); if (v !== null && v < min) min = v; }
      result[ci] = min === Infinity ? null : min;
    } else if (agg === "max") {
      let max = -Infinity;
      for (const row of rows) { const v = asFiniteNumber(row[ci]); if (v !== null && v > max) max = v; }
      result[ci] = max === -Infinity ? null : max;
    } else if (agg === "count") {
      result[ci] = count;
    } else {
      result[ci] = sum;
    }
  }

  return result;
}
